import { useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import { NOTE_BODY_MAX_LENGTH } from '@tailoring-crm/shared';
import { Button, useConfirm, Textarea, ListSkeleton, useToast } from '@tailoring-crm/ui';
import {
  useCreateNote,
  useDeleteNote,
  useNotes,
  useUpdateNote,
  type ActivityNote,
  type ActivitySubject,
} from '../../lib/activity/use-activity.js';
import { formatDateTime } from '../../lib/format.js';

/**
 * What the shop knows about this person — a customer's notes.
 *
 * A composer pinned at the top and the notes under it, newest first — the opposite arrangement
 * from the timeline next door, because this is a place people WRITE rather than read. The box is
 * the first thing under the tab, so adding a note is one click from opening it.
 *
 * Each note is its own record with an author and a time, so "quoted 8k on the 10th" survives the
 * next person to type something.
 *
 * Editing is allowed and VISIBLE: an edited note is labelled, with the time it changed. Deleting
 * is soft on the server — the row leaves this list and stays in the database. That combination is
 * why notes are editable at all while calls are frozen; see `schemas/customer-follow-ups.ts`.
 */

export interface NotesThreadProps {
  subject: ActivitySubject;
  /** Read, never written on — no composer, no edit, no delete. */
  readOnly?: boolean;
}

export function NotesThread({ subject, readOnly = false }: NotesThreadProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();

  const notes = useNotes(subject);
  const create = useCreateNote(subject);
  const update = useUpdateNote(subject);
  const remove = useDeleteNote(subject);

  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const submitNew = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;

    create.mutate(
      { body },
      {
        onSuccess: () => {
          setDraft('');
          toast({ title: t('activity.notes.added'), variant: 'success' });
        },
        onError: () => toast({ title: t('error.unexpected'), variant: 'destructive' }),
      },
    );
  };

  const saveEdit = (noteId: string): void => {
    const body = editDraft.trim();
    if (!body) return;

    update.mutate(
      { noteId, input: { body } },
      {
        onSuccess: () => {
          setEditingId(null);
          toast({ title: t('activity.notes.updated'), variant: 'success' });
        },
        onError: () => toast({ title: t('error.unexpected'), variant: 'destructive' }),
      },
    );
  };

  const deleteNote = (note: ActivityNote): void => {
    void (async () => {
      const confirmed = await confirm({
        title: t('activity.notes.deleteTitle'),
        description: t('activity.notes.deleteDescription'),
        confirmLabel: t('common.delete'),
        destructive: true,
      });
      if (!confirmed) return;
      await remove.mutateAsync(note.id);
      toast({ title: t('activity.notes.deleted'), variant: 'success' });
    })();
  };

  const items = notes.data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      {readOnly ? null : (
        <form onSubmit={submitNew} className="flex flex-col gap-2">
          <Textarea
            rows={3}
            value={draft}
            maxLength={NOTE_BODY_MAX_LENGTH}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('activity.notes.placeholder')}
            aria-label={t('activity.notes.newLabel')}
          />
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={!draft.trim() || create.isPending}>
              {t('activity.notes.add')}
            </Button>
          </div>
        </form>
      )}

      {notes.isPending ? (
        <ListSkeleton rows={3} />
      ) : items.length === 0 ? (
        <p className="activity-empty rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t('activity.notes.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((note) => (
            <li key={note.id} className="activity-note rounded-lg border border-border p-3">
              {editingId === note.id ? (
                <div className="flex flex-col gap-2">
                  <Textarea
                    rows={3}
                    value={editDraft}
                    maxLength={NOTE_BODY_MAX_LENGTH}
                    onChange={(event) => setEditDraft(event.target.value)}
                    aria-label={t('activity.notes.editLabel')}
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                      {t('common.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      disabled={!editDraft.trim() || update.isPending}
                      onClick={() => saveEdit(note.id)}
                    >
                      {t('common.save')}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* The body first and the attribution under it: the note is what a reader came
                      for, and who wrote it is context they only sometimes want. */}
                  <p className="whitespace-pre-wrap break-words text-sm">{note.body}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                    <span>{note.authorName ?? t('activity.notes.unknownAuthor')}</span>
                    <span aria-hidden="true">·</span>
                    <span>{formatDateTime(note.createdAt)}</span>
                    {/* An edit is never hidden. This label is the whole reason notes are allowed
                        to be editable while calls are not. */}
                    {note.editedAt ? (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>
                          {t('activity.notes.edited', { at: formatDateTime(note.editedAt) })}
                        </span>
                      </>
                    ) : null}
                    {readOnly ? null : (
                      <span className="ml-auto flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t('activity.notes.editAction')}
                          onClick={() => {
                            setEditingId(note.id);
                            setEditDraft(note.body);
                          }}
                        >
                          <Pencil aria-hidden="true" className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t('activity.notes.deleteAction')}
                          onClick={() => deleteNote(note)}
                        >
                          <Trash2 aria-hidden="true" className="size-3.5" />
                        </Button>
                      </span>
                    )}
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
